import { trpc } from "../../lib/trpc";

export const CURRENCY_OPTS: Array<{ v: string; label: string }> = [
  { v: "TRY", label: "₺ TL" },
  { v: "USD", label: "$ USD" },
  { v: "EUR", label: "€ EUR" },
  { v: "GBP", label: "£ GBP" },
];

export const PAYMENT_METHODS: Array<{ v: string; label: string }> = [
  { v: "cash", label: "Peşin" },
  { v: "transfer", label: "Havale / EFT" },
  { v: "credit_card", label: "Kredi Kartı" },
  { v: "term", label: "Vadeli / Açık Hesap" },
];
export const PAYMENT_METHOD_LABEL: Record<string, string> = Object.fromEntries(PAYMENT_METHODS.map((m) => [m.v, m.label]));

export interface PaymentTerms {
  currency: string;
  paymentMethod: string; // "" | cash | transfer | credit_card | term
  termDays: string;
  installmentCount: string;
  bankIds: string[];
  paymentNotes: string;
}

export const emptyPaymentTerms: PaymentTerms = { currency: "TRY", paymentMethod: "", termDays: "", installmentCount: "", bankIds: [], paymentNotes: "" };

/** Talep/PO ödeme vadesi alanları — para birimi + yöntem + koşullu (kredi kartı: taksit+banka / vadeli: gün). */
export function PaymentTermsFields({ value, onChange, enabled }: { value: PaymentTerms; onChange: (v: PaymentTerms) => void; enabled: boolean }) {
  // includeInactive: seçili ama pasifleştirilmiş banka "görünmez seçim" olarak kalmasın (düzenlemede kaldırılabilsin).
  const banksQ = trpc.settings.bank.list.useQuery({ includeInactive: true }, { enabled, staleTime: 60_000 });
  const allBanks = banksQ.data ?? [];
  // Picker: aktif bankalar + hâlihazırda seçili olan pasif bankalar (pasif olanlar "(pasif)" işaretli, yeni seçime çıkmaz).
  const banks = allBanks.filter((b) => b.isActive || value.bankIds.includes(b.id));
  const set = (patch: Partial<PaymentTerms>) => onChange({ ...value, ...patch });
  const isCard = value.paymentMethod === "credit_card";
  const isTerm = value.paymentMethod === "term";
  const toggleBank = (id: string) => set({ bankIds: value.bankIds.includes(id) ? value.bankIds.filter((x) => x !== id) : [...value.bankIds, id] });

  return (
    <>
      {/* Para birimi + yöntem + koşullu (vade/taksit) + not — tek sıkı ızgara */}
      <div className="po-fgrid">
        <div className="form-field">
          <label className="lbl" htmlFor="pt-cur">Para Birimi</label>
          <select id="pt-cur" className="form-select" value={value.currency} onChange={(e) => set({ currency: e.target.value })}>
            {CURRENCY_OPTS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="pt-method">Ödeme Yöntemi</label>
          <select id="pt-method" className="form-select" value={value.paymentMethod} onChange={(e) => set({ paymentMethod: e.target.value })}>
            <option value="">— Belirtilmedi —</option>
            {PAYMENT_METHODS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
          </select>
        </div>
        {isTerm ? (
          <div className="form-field">
            <label className="lbl" htmlFor="pt-term">Vade (net gün)</label>
            <input id="pt-term" className="form-input" type="number" min={0} max={3650} value={value.termDays} onChange={(e) => set({ termDays: e.target.value })} placeholder="örn. 30" />
          </div>
        ) : null}
        {isCard ? (
          <div className="form-field">
            <label className="lbl" htmlFor="pt-inst">Taksit Sayısı</label>
            <input id="pt-inst" className="form-input" type="number" min={1} max={60} value={value.installmentCount} onChange={(e) => set({ installmentCount: e.target.value })} placeholder="örn. 6" />
          </div>
        ) : null}
        {value.paymentMethod ? (
          <div className="form-field">
            <label className="lbl" htmlFor="pt-notes">Ödeme Notu</label>
            <input id="pt-notes" className="form-input" value={value.paymentNotes} onChange={(e) => set({ paymentNotes: e.target.value })} maxLength={300} placeholder="opsiyonel" />
          </div>
        ) : null}
      </div>

      {isCard ? (
        <div className="form-field" style={{ marginBottom: 10 }}>
          <label className="lbl">Anlaşmalı Banka(lar)</label>
          {banks.length === 0 ? (
            <span className="help">Tanımlı banka yok — Ayarlar → Bankalar'dan ekleyin.</span>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {banks.map((b) => {
                const on = value.bankIds.includes(b.id);
                return (
                  <button type="button" key={b.id} onClick={() => toggleBank(b.id)}
                    className={`badge-pill ${on ? "b-info" : ""}`}
                    style={{ cursor: "pointer", border: on ? undefined : "1px solid var(--neutral-300)", background: on ? undefined : "var(--white)" }}>
                    {on ? "✓ " : ""}{b.name}{b.isActive ? "" : " (pasif)"}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}

/** PaymentTerms state → API input (boş/ilgisiz alanlar null). */
export function paymentTermsToInput(pt: PaymentTerms) {
  const method = pt.paymentMethod || null;
  return {
    currency: pt.currency as "TRY" | "USD" | "EUR" | "GBP",
    paymentMethod: method as "cash" | "transfer" | "credit_card" | "term" | null,
    installmentCount: method === "credit_card" && pt.installmentCount ? Math.floor(Number(pt.installmentCount)) : null,
    termDays: method === "term" && pt.termDays !== "" ? Math.floor(Number(pt.termDays)) : null,
    bankIds: method === "credit_card" ? pt.bankIds : [],
    paymentNotes: method ? (pt.paymentNotes.trim() || null) : null,
  };
}
